import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  FormGroup, FormControlLabel, Checkbox, Chip, Stack, CircularProgress, Alert, IconButton, Tooltip,
} from '@mui/material';
import CropIcon from '@mui/icons-material/Crop';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../contexts/AuthContext';
import {
  CameraUploadImage,
  getCameraUploadImageFileUrl,
  fetchCameraUploadImageBlob,
  patchCameraUploadClassifications,
  addCameraUploadClassificationBox,
  adjustCameraUploadClassificationBox,
  deleteCameraUploadClassificationBox,
} from '../../api/cameraUpload';
import BoundingBoxOverlay, { OverlayBox } from './BoundingBoxOverlay';
import PushToTrainingDataButton from './PushToTrainingDataButton';

interface ImageCorrectionDialogProps {
  deviceId: string;
  image: CameraUploadImage | null;
  availableClasses: string[];
  onClose: () => void;
  onSaved: () => void;
  // For box draw/adjust/delete — refreshes the folder tree/grid in the
  // background without closing the dialog, unlike onSaved (which is for the
  // explicit Save button, where closing is the point).
  onBackgroundChange: () => void;
  onBack: () => void;
  onNext: () => void;
  hasBack: boolean;
  hasNext: boolean;
}

// Stable, readable palette cycled by class name so a class keeps the same
// color across images/dialogs (helps recognize it at a glance).
const BOX_COLORS = ['#e53935', '#1e88e5', '#43a047', '#fb8c00', '#8e24aa', '#00897b', '#d81b60', '#5e35b1'];
function colorForClass(className: string): string {
  let hash = 0;
  for (let i = 0; i < className.length; i++) hash = (hash * 31 + className.charCodeAt(i)) | 0;
  return BOX_COLORS[Math.abs(hash) % BOX_COLORS.length];
}

const ImageCorrectionDialog: React.FC<ImageCorrectionDialogProps> = ({ deviceId, image, availableClasses, onClose, onSaved, onBackgroundChange, onBack, onNext, hasBack, hasNext }) => {
  const auth = useAuth();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [currentImage, setCurrentImage] = useState<CameraUploadImage | null>(image);
  const [drawingClassName, setDrawingClassName] = useState<string | null>(null);
  const [boxError, setBoxError] = useState('');

  useEffect(() => {
    if (image) {
      setSelected(new Set(image.classifications.map((c) => c.className)));
      setCurrentImage(image);
      setDrawingClassName(null);
      setError('');
      setBoxError('');
    }
  }, [image]);

  // The file endpoint requires an Authorization header, which a plain
  // <img src> can't send — fetch it as a blob instead, same as the
  // thumbnail grid does.
  useEffect(() => {
    if (!image) {
      setImageUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    fetchCameraUploadImageBlob(getCameraUploadImageFileUrl(deviceId, image.id), auth.tokens?.accessToken ?? '')
      .then((url) => {
        if (cancelled) { URL.revokeObjectURL(url); return; }
        objectUrl = url;
        setImageUrl(url);
      })
      .catch(() => { if (!cancelled) setImageUrl(null); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [deviceId, image, auth.tokens?.accessToken]);

  const originalClasses = useMemo(() => new Set(image?.classifications.map((c) => c.className) ?? []), [image]);
  // A class can now have several rows (one per box instance) — grouped so
  // the sidebar can list each instance under its class.
  const rowsByClass = useMemo(() => {
    const map = new Map<string, CameraUploadImage['classifications']>();
    currentImage?.classifications.forEach((c) => {
      const list = map.get(c.className) ?? [];
      list.push(c);
      map.set(c.className, list);
    });
    return map;
  }, [currentImage]);

  const allClassNames = useMemo(() => {
    const names = new Set(availableClasses);
    image?.classifications.forEach((c) => names.add(c.className));
    return Array.from(names).sort();
  }, [availableClasses, image]);

  const overlayBoxes: OverlayBox[] = useMemo(() => {
    if (!currentImage) return [];
    return currentImage.classifications
      .filter((c) => c.boxX1 != null && c.boxY1 != null && c.boxX2 != null && c.boxY2 != null)
      .map((c) => ({
        id: c.id,
        className: c.className,
        x1: c.boxX1 as number,
        y1: c.boxY1 as number,
        x2: c.boxX2 as number,
        y2: c.boxY2 as number,
        color: colorForClass(c.className),
      }));
  }, [currentImage]);

  const toggleClass = (className: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(className)) next.delete(className);
      else next.add(className);
      return next;
    });
  };

  // Saves any pending checkbox changes, if there are any. Returns whether
  // it's safe to proceed (nothing to save, or the save succeeded).
  const commitPendingChanges = async (): Promise<boolean> => {
    if (!image) return true;
    const add = Array.from(selected).filter((c) => !originalClasses.has(c));
    const remove = Array.from(originalClasses).filter((c) => !selected.has(c));
    if (add.length === 0 && remove.length === 0) return true;
    setSaving(true);
    setError('');
    try {
      await patchCameraUploadClassifications(deviceId, image.id, add, remove, auth.tokens?.accessToken ?? '');
      return true;
    } catch (err: any) {
      setError(err?.message || 'Failed to save classification');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (await commitPendingChanges()) onSaved();
  };

  // Back/Next save any pending checkbox changes (like Save) but refresh the
  // grid in the background and keep the dialog open on the adjacent image,
  // rather than closing it.
  const handleNav = async (direction: 'back' | 'next') => {
    if (!(await commitPendingChanges())) return;
    onBackgroundChange();
    if (direction === 'back') onBack(); else onNext();
  };

  // Always creates a NEW box instance for the class being drawn — this is
  // how a 2nd/3rd instance of the same species (e.g. another wallaby) gets
  // its own box, instead of overwriting the first one.
  const handleBoxDrawn = async (className: string, box: { x1: number; y1: number; x2: number; y2: number }) => {
    if (!image) return;
    setBoxError('');
    setDrawingClassName(null);
    try {
      const updated = await addCameraUploadClassificationBox(deviceId, image.id, className, box, auth.tokens?.accessToken ?? '');
      setCurrentImage(updated);
      setSelected(new Set(updated.classifications.map((c) => c.className)));
      onBackgroundChange();
    } catch (err: any) {
      setBoxError(err?.message || 'Failed to add bounding box');
    }
  };

  // Adjusts one existing box instance in place (drag/resize) — addressed by
  // row id since a class can have several boxes.
  const handleBoxChanged = async (classificationId: string, box: { x1: number; y1: number; x2: number; y2: number }) => {
    if (!image) return;
    setBoxError('');
    try {
      const updated = await adjustCameraUploadClassificationBox(deviceId, image.id, classificationId, box, auth.tokens?.accessToken ?? '');
      setCurrentImage(updated);
      setSelected(new Set(updated.classifications.map((c) => c.className)));
      onBackgroundChange();
    } catch (err: any) {
      setBoxError(err?.message || 'Failed to update bounding box');
    }
  };

  // Removes one box instance immediately — unlike the checkbox list, which
  // batches removals behind the Save button, this matches box-drawing's
  // instant-save feel so a wrong box can be cleared in one click. Leaves any
  // other instances of the same class untouched.
  const handleDeleteBox = async (classificationId: string) => {
    if (!image) return;
    setBoxError('');
    try {
      const updated = await deleteCameraUploadClassificationBox(deviceId, image.id, classificationId, auth.tokens?.accessToken ?? '');
      setCurrentImage(updated);
      setSelected(new Set(updated.classifications.map((c) => c.className)));
      onBackgroundChange();
    } catch (err: any) {
      setBoxError(err?.message || 'Failed to remove box');
    }
  };

  // Un-tags a class entirely (all its box instances, or its boxless tag) —
  // this is the checkbox-adjacent trash icon, distinct from deleting one box.
  const handleDeleteClass = async (className: string) => {
    if (!image) return;
    setBoxError('');
    if (drawingClassName === className) setDrawingClassName(null);
    try {
      const updated = await patchCameraUploadClassifications(deviceId, image.id, [], [className], auth.tokens?.accessToken ?? '');
      setCurrentImage(updated);
      setSelected(new Set(updated.classifications.map((c) => c.className)));
      onBackgroundChange();
    } catch (err: any) {
      setBoxError(err?.message || 'Failed to remove classification');
    }
  };

  const hasDims = !!(currentImage?.width && currentImage?.height);

  return (
    <Dialog open={!!image} onClose={onClose} fullScreen>
      <DialogTitle>
        {image?.originalFileName}
        {image?.capturedAt && (
          <Typography variant="caption" color="text.secondary" display="block">
            Captured {new Date(image.capturedAt).toLocaleString()}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {image && (
          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                bgcolor: 'action.hover',
                minHeight: { xs: 300, md: 'calc(100vh - 220px)' },
              }}
            >
              {imageUrl && hasDims ? (
                <BoundingBoxOverlay
                  imageUrl={imageUrl}
                  imageWidth={currentImage!.width as number}
                  imageHeight={currentImage!.height as number}
                  boxes={overlayBoxes}
                  drawingClassName={drawingClassName}
                  drawingColor={drawingClassName ? colorForClass(drawingClassName) : undefined}
                  onBoxDrawn={handleBoxDrawn}
                  onBoxChanged={handleBoxChanged}
                />
              ) : (
                <Box
                  component="img"
                  src={imageUrl ?? undefined}
                  alt={image.originalFileName}
                  sx={{ maxWidth: '100%', maxHeight: 'calc(100vh - 220px)', objectFit: 'contain' }}
                />
              )}
            </Box>
            <Box sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Classifications</Typography>
              {!hasDims && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  This photo has no stored dimensions (uploaded before box-drawing support) — re-upload to draw boxes on it.
                </Alert>
              )}
              {drawingClassName && (
                <Alert severity="info" sx={{ mb: 1 }} onClose={() => setDrawingClassName(null)}>
                  Drag on the image to draw a box for "{drawingClassName}"
                </Alert>
              )}
              <FormGroup>
                {allClassNames.map((className) => {
                  const rows = rowsByClass.get(className) ?? [];
                  const boxedRows = rows.filter((c) => c.boxX1 != null && c.boxY1 != null && c.boxX2 != null && c.boxY2 != null);
                  const hasBox = boxedRows.length > 0;
                  return (
                    <Box key={className}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                          sx={{ flexGrow: 1 }}
                          control={<Checkbox checked={selected.has(className)} onChange={() => toggleClass(className)} />}
                          label={
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Box component="span" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colorForClass(className), display: hasBox ? 'inline-block' : 'none' }} />
                              <span>{className}</span>
                              {boxedRows.length > 1 && (
                                <Chip size="small" label={`${boxedRows.length} boxes`} variant="outlined" />
                              )}
                              {boxedRows.length === 1 && (
                                <Chip
                                  size="small"
                                  label={boxedRows[0].confidence != null ? `${boxedRows[0].source} ${Math.round(boxedRows[0].confidence * 100)}%` : boxedRows[0].source}
                                  variant="outlined"
                                />
                              )}
                            </Stack>
                          }
                        />
                        {hasDims && selected.has(className) && (
                          <Tooltip title={hasBox ? 'Add another box (e.g. a 2nd animal of this class)' : 'Draw box'}>
                            <IconButton size="small" onClick={() => setDrawingClassName(className)}>
                              <CropIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {rows.length > 0 && (
                          <Tooltip title="Remove this classification and all its boxes">
                            <IconButton size="small" onClick={() => handleDeleteClass(className)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      {boxedRows.length > 1 && (
                        <Stack sx={{ pl: 5, mb: 0.5 }}>
                          {boxedRows.map((row, idx) => (
                            <Box key={row.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography variant="caption" color="text.secondary">Box {idx + 1}</Typography>
                              <IconButton size="small" onClick={() => handleDeleteBox(row.id)}>
                                <DeleteIcon fontSize="inherit" />
                              </IconButton>
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  );
                })}
              </FormGroup>
              {allClassNames.length === 0 && (
                <Typography color="text.secondary" variant="body2">
                  This device's inference model has no classes configured.
                </Typography>
              )}
              {boxError && <Alert severity="error" sx={{ mt: 1 }}>{boxError}</Alert>}
            </Box>
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
          {currentImage && <PushToTrainingDataButton deviceId={deviceId} image={currentImage} />}
        </Box>
        <Box>
          <Button onClick={() => handleNav('back')} disabled={saving || !hasBack}>Back</Button>
          <Button onClick={() => handleNav('next')} disabled={saving || !hasNext} sx={{ mr: 1 }}>Next</Button>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ ml: 1 }}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCorrectionDialog;
